import { createApi } from '@reduxjs/toolkit/query/react'
import {
  AddEntityRequest,
  CustomFieldsRequest,
  EditEntityRequest,
  EntityDetailResponse,
  EntityListRequest,
  EntityListResponse,
  EntitySummaryResponse,
  MutationResponse,
  DeleteEntityRequest,
  ServiceDayDetailResponse,
  ServiceDayDetailRequest,
  ServiceDayListResponse,
  ServiceDayListTransformedResponse,
  ServiceDayListRequest,
  BulkAddOrderRequest,
  BulkAddServicePointRequest,
  BulkDeleteEntityRequest,
  BulkActionResponse,
  SearchEntityRequest,
  SearchEntityResponse,
  ExportRequest,
  CustomFieldEngResponse,
  AllServicePointsOfEntityRequest,
  UnlinkServicePointRequest,
  EntityContactResponse,
  UpdateStatusRequest,
  TableResponseWithTimezone,
  EntityDetailRequest,
} from './types'
import axiosBaseQuery from 'services/axiosBaseQuery'
import { sendNotification } from 'components'
import { sendExportNotification, sendImportNotification } from 'utils'
import i18n from 'utils/i18n'
import { GenericResponse, TableRequestV2 } from 'services/types'
import { servicePointApi } from './servicePoint'
import { orderApi } from './order'
import { assetApi } from 'services/AssetManagement'
import { reportsApi } from 'services/ReportManagement/reports'

export const entityApi = createApi({
  reducerPath: 'entityApi',
  tagTypes: [
    'Entities',
    'EntityDetail',
    'Summary',
    'EntityHistory',
    'Contacts',
    'ContactDetail',
    'EntityContacts',
    'ListAllServicePointsOfEntity',
    'ServiceDays',
    'ServiceDayDetails',
    'EntityOrdersExternal',
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${window.envVariables.REACT_APP_BASE_URL + '/'}`,
  }),
  endpoints: (builder) => ({
    getEntitySummary: builder.query<EntitySummaryResponse, string>({
      query: (client_id) => ({
        url: `engagement/clients/${client_id}/entity/summary/`,
        method: 'GET',
      }),
      providesTags: ['Summary'],
    }),
    getEntityList: builder.query<EntityListResponse, EntityListRequest>({
      query: ({ client_id, ...data }) => ({
        url: `engagement/clients/${client_id}/entity/list/`,
        method: 'POST',
        data,
      }),
      providesTags: ['Entities'],
    }),
    unlinkServicePoint: builder.mutation<MutationResponse, UnlinkServicePointRequest>({
      query: ({ client_id, entity_id, body }) => ({
        url: `/engagement/clients/${client_id}/entity/${entity_id}/unlink/service_point/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [
        'ListAllServicePointsOfEntity',
        'ServiceDays',
        'ServiceDayDetails',
        'EntityHistory',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(
            servicePointApi.util.invalidateTags([
              'ServicePoints',
              'ServicePointLocations',
              'ServicePointDetail',
            ]),
          )
          dispatch(assetApi.util.invalidateTags(['Assets', 'AssetDetail']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    deleteEntity: builder.mutation<MutationResponse, DeleteEntityRequest>({
      query: ({ client_id, entity_id, ...data }) => ({
        url: `engagement/clients/${client_id}/entity/${entity_id}/`,
        method: 'DELETE',
        data,
      }),
      invalidatesTags: ['Entities', 'Summary'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    searchEntity: builder.mutation<SearchEntityResponse, SearchEntityRequest>({
      query: ({ client_id, external_client_id, ...data }) => ({
        url: client_id
          ? `engagement/clients/${client_id}/entity/search/`
          : `engagement/external/clients/${external_client_id}/entity/search/`,
        method: 'POST',
        data,
      }),
    }),
    getEntityDetail: builder.query<EntityDetailResponse, EntityDetailRequest>({
      query: ({ client_id, entity_id }) => ({
        url: `engagement/clients/${client_id}/entity/`,
        method: 'GET',
        params: {
          entity_id,
        },
      }),
      providesTags: ['EntityDetail'],
    }),
    getCustomFields: builder.query<CustomFieldEngResponse, CustomFieldsRequest>({
      query: ({ clientId, type_id, ...data }) => ({
        url: `engagement/clients/${clientId}/entity_dynamic_fields/`,
        method: 'GET',
        data,
        params: {
          entity_type_id: type_id,
        },
      }),
    }),
    addEntity: builder.mutation<
      MutationResponse & { entity_id: string; entity_type: string },
      AddEntityRequest
    >({
      query: ({ clientId, body }) => ({
        url: `engagement/clients/${clientId}/entity/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Entities', 'EntityHistory'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
            hyperLink: {
              type: 'newTab',
              label: i18n.t('response.see_more'),
              to: `/engagement/entities/detail/${data.entity_type}/${data.entity_id}`,
            },
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    editEntity: builder.mutation<
      MutationResponse & { entity_id: string; entity_type: string },
      EditEntityRequest
    >({
      query: ({ clientId, entity_id, body }) => ({
        url: `engagement/clients/${clientId}/entity/${entity_id}/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [
        'Entities',
        'EntityDetail',
        'EntityHistory',
        'ServiceDays',
        'ServiceDayDetails',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
            hyperLink: {
              type: 'newTab',
              label: i18n.t('response.see_more'),
              to: `/engagement/entities/detail/${data.entity_type}/${data.entity_id}`,
            },
          })
          dispatch(
            orderApi.util.invalidateTags(['OrderPlanDetails', 'OrderPlans', 'OrderSettings']),
          )
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    getServiceDayList: builder.query<ServiceDayListTransformedResponse, ServiceDayListRequest>({
      query: ({ client_id, start_date, end_date, entity_id, service_point_id }) => ({
        url: `engagement/clients/${client_id}/service_day/list/`,
        method: 'GET',
        params: {
          start_date,
          end_date,
          entity_id,
          service_point_id,
        },
      }),
      transformResponse: ({ data }: ServiceDayListResponse) => {
        const parsedList = []
        for (const [key, value] of Object.entries(data))
          parsedList.push({ date: new Date(key).toUTCString(), types: value })
        return parsedList
      },
      providesTags: ['ServiceDays'],
    }),
    getServiceDayDetail: builder.query<ServiceDayDetailResponse, ServiceDayDetailRequest>({
      query: ({ client_id, date, entity_id, service_point_id }) => ({
        url: `engagement/clients/${client_id}/service_day/detail/`,
        method: 'GET',
        params: {
          date,
          entity_id,
          service_point_id,
        },
      }),
      providesTags: ['ServiceDayDetails'],
    }),
    bulkAddOrder: builder.mutation<BulkActionResponse, BulkAddOrderRequest>({
      query: ({ clientId, ...data }) => ({
        url: `engagement/clients/${clientId}/entity/bulk_edit/order/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['EntityDetail', 'ServiceDays', 'ServiceDayDetails', 'EntityOrdersExternal'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    bulkAddServicePoint: builder.mutation<BulkActionResponse, BulkAddServicePointRequest>({
      query: ({ clientId, ...data }) => ({
        url: `engagement/clients/${clientId}/entity/bulk_edit/service_point/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ListAllServicePointsOfEntity', 'ServiceDays', 'ServiceDayDetails'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
          dispatch(servicePointApi.util.invalidateTags(['ServicePoints', 'ServicePointLocations']))
          dispatch(assetApi.util.invalidateTags(['Assets', 'AssetDetail']))
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    bulkDeleteEntity: builder.mutation<BulkActionResponse, BulkDeleteEntityRequest>({
      query: ({ clientId, ...data }) => ({
        url: `engagement/clients/${clientId}/entity/bulk_delete/`,
        method: 'DELETE',
        data,
      }),
      invalidatesTags: ['Entities', 'Summary'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // `onSuccess` side-effect
          if (data.failure_list.length === 0) {
            sendNotification({
              type: 'success',
              toastContent: data.detail.message,
            })
          } else if (data.successful_list.length === 0) {
            sendNotification({
              type: 'error',
              toastContent: data.detail.message,
            })
          } else {
            let failure_list_items = data.detail.message
            data.failure_list.forEach((item) => (failure_list_items += item.description))

            sendNotification({
              type: 'warning',
              toastContent: failure_list_items,
            })
          }

          sendNotification({
            type: 'success',
            toastContent: data.detail.message,
          })
        } catch (err) {
          // `onError` side-effect
        }
      },
    }),
    exportEntity: builder.mutation<MutationResponse, ExportRequest & { entity_type_id?: string }>({
      query: ({ client_id, ...data }) => ({
        url: `engagement/clients/${client_id}/entity/export/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendExportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ExportReports']))
        } catch (error) {}
      },
    }),
    listAllServicePointsOfEntity: builder.query<
      MutationResponse & {
        service_point_list: Array<{ service_point_id: string; service_point_name: string }>
      },
      AllServicePointsOfEntityRequest
    >({
      query: ({ external_client_id, entity_id }) => ({
        url: `engagement/clients/${external_client_id}/entity/related_list/service_points/`,
        method: 'GET',
        params: {
          entity_id,
        },
      }),
      providesTags: ['ListAllServicePointsOfEntity'],
    }),
    getEntityOrdersExternal: builder.query<
      { order_list: Array<{ value: string; label: string }> },
      { client_id: string; entity_id: string }
    >({
      query: ({ client_id, entity_id }) => ({
        url: `engagement/external/clients/${client_id}/entity/${entity_id}/list/orders/`,
        method: 'GET',
      }),
      providesTags: ['EntityOrdersExternal'],
    }),
    getEntityContacts: builder.query<
      EntityContactResponse,
      { client_id: string; entity_id: string }
    >({
      query: ({ client_id, entity_id }) => ({
        url: `engagement/clients/${client_id}/entity/related_list/entity_contact/`,
        method: 'GET',
        params: {
          entity_id,
        },
      }),
      providesTags: ['EntityContacts'],
    }),
    importEntities: builder.mutation<MutationResponse, { clientId: string; body: FormData }>({
      query: ({ clientId, body }) => ({
        url: `engagement/clients/${clientId}/entity/import/`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Entities', 'Summary'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          sendImportNotification(data.detail.message)
          dispatch(reportsApi.util.invalidateTags(['ImportReports']))
        } catch (error) {}
      },
    }),
    entityHistory: builder.query<
      TableResponseWithTimezone<{}>,
      TableRequestV2 & {
        entity_id: string
      }
    >({
      query: ({ client_id, entity_id, ...data }) => ({
        url: `engagement/clients/${client_id}/entity/${entity_id}/history/`,
        method: 'POST',
        data,
      }),
      providesTags: ['EntityHistory'],
      transformResponse: (response: TableResponseWithTimezone<{}>, _, { entity_id }) => {
        if (entity_id !== '38bbd566-60da-45f2-8a5c-dbc75783a71b') {
          //isClientDummy isDummyClient
          return response
        }
        return {
          ...response,
          data: [
            {
              id: 1,
              operator: 'semih.albayrak1',
              date: '2024-11-11T07:57:56.015Z',
              change_type: [2, 'Klant gecreëerd'],
            },
            {
              id: 2,
              operator: 'alex.demo',
              date: '2025-11-20T08:59:00.460Z',
              change_type: [3, 'Entity Updated'],
              changed_list: [
                {
                  before: null,
                  after: '158100057337',
                  type: 'TEXT',
                  label: 'External-Id',
                  key: 'external-id',
                },
              ],
            },
            {
              id: 3,
              operator: 'alex.demo',
              date: '2025-01-01T11:57:56.015Z',
              change_type: [2, 'Pas gekoppelde Pas-ID: 50158100003986'],
            },
            {
              id: 4,
              operator: 'semih.albayrak1',
              date: '2025-10-04T15:59:00.460Z',
              change_type: [3, 'Entity Updated'],
              changed_list: [
                {
                  before: 'Gobbeland',
                  after: 'Welwaerdeken',
                  type: 'TEXT',
                  label: 'Openbare Ruimte Naam',
                  key: 'openbare-ruimte-naam',
                },
              ],
            },
            {
              id: 5,
              operator: 'alex.demo',
              date: '2025-05-02T13:22:00.460Z',
              change_type: [3, 'Entity Updated'],
              changed_list: [
                {
                  before: {
                    color: 'green',
                    label: 'Actief',
                  },
                  after: {
                    color: 'red',
                    label: 'Stuk',
                  },
                  type: 'SHEET',
                  label: 'Status',
                  key: 'status',
                },
              ],
            },
            {
              id: 6,
              operator: 'alex.demo',
              date: '2025-05-02T16:48:56.015Z',
              change_type: [2, 'Pas gekoppelde Pas-ID: 50158100004974'],
            },
          ],
        }
      },
    }),
    updateEntityStatus: builder.mutation<MutationResponse, UpdateStatusRequest>({
      query: ({ client_id, ...data }) => ({
        url: `engagement/clients/${client_id}/status/update/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Entities', 'EntityDetail', 'EntityHistory'],
    }),
    attachBulkSpV2: builder.mutation<
      GenericResponse & {
        failure_list: Array<{
          id: string
          name: string
          type: 'entity_not_found' | 'service_point_not_found' | 'unknown'
        }>
        success_list: Array<{
          id: string
          name: string
        }>
      },
      {
        entity_id_list: Array<string>
        client_id: string
        service_point_id_list: Array<string>
        overwrite: boolean
      }
    >({
      query: ({ client_id, ...data }) => ({
        url: `engagement/clients/${client_id}/v2/entity/bulk_edit/service_point/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ListAllServicePointsOfEntity', 'Entities'],
      onQueryStarted: async (_, { dispatch }) => {
        try {
          dispatch(
            servicePointApi.util.invalidateTags([
              'ServicePointDetail',
              'ServicePointHistory',
              'ServicePoints',
            ]),
          )
        } catch (error) {
          console.log(error)
        }
      },
    }),
  }),
})

export const {
  useGetCustomFieldsQuery,
  useAddEntityMutation,
  useEditEntityMutation,
  useGetEntityListQuery,
  useDeleteEntityMutation,
  useGetEntityDetailQuery,
  useGetEntitySummaryQuery,
  useGetServiceDayListQuery,
  useGetServiceDayDetailQuery,
  useBulkAddServicePointMutation,
  useBulkDeleteEntityMutation,
  useSearchEntityMutation,
  useExportEntityMutation,
  useListAllServicePointsOfEntityQuery,
  useUnlinkServicePointMutation,
  useGetEntityOrdersExternalQuery,
  useGetEntityContactsQuery,
  useImportEntitiesMutation,
  useEntityHistoryQuery,
  useUpdateEntityStatusMutation,
  useAttachBulkSpV2Mutation,
  useLazyGetEntityDetailQuery,
} = entityApi
